/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Autocomplete, Badge, Button, Input, Modal } from "src/components";
import Table from "src/features/fields/components/Table";
import UploadIcon from "src/assets/upload.svg";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import useGranularPermission from "src/hooks/useGranularPermission";
import { useToast } from "src/hooks/useToast";
import { getAllCompanyDetails } from "src/services/company-api";
import { getCompanyItemPlantStock, uploadStockPosition } from "src/services/stock-position-api";

interface StockRow {
  company_item_plant_stock_id?: number;
  item_code: string;
  plant_code: string;
  division_shortcode: string;
  stock_available: number;
}

const columnHelper = createColumnHelper<StockRow>();

// "Stock Position" tab under Transactions > Purchase Order. Pick a company to view its
// stock position rows (company_item_plant_stock); search filters item/plant/division.
// Users with the Edit right (writer/editor on po:stock-position) also get an Upload
// button beside the search that uploads the company-provided stock file.
const StockPosition = () => {
  const { toast } = useToast();
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<StockRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Upload modal (Edit only) — company is already selected on the tab, so just the file.
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Confirm step before the destructive full-refresh upload.
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // Item codes returned by the backend that aren't configured as alternate item codes
  // for the company — shown as a non-blocking warning list inside the upload modal.
  const [warnings, setWarnings] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const closeUploadModal = () => {
    setIsUploadOpen(false);
    setIsConfirmOpen(false);
    setFile(null);
    setWarnings([]);
  };

  const { canEdit } = useGranularPermission({
    module: fineGrainedPermissionsKeys.PURCHASEORDER.STOCKPOSITION.module,
    action: fineGrainedPermissionsKeys.PURCHASEORDER.STOCKPOSITION.action,
  });

  useEffect(() => {
    getAllCompanyDetails()
      .then((res: any) => setCompanies((res?.data || []).filter((company: any) => company?.alternate_item_code_applicable)))
      .catch((error: any) => {
        console.error(error);
        toast({ variant: "destructive", description: "Failed to load companies" });
      });
  }, []);

  const fetchStock = (companyCode?: number | string, searchTerm?: string) => {
    const code = companyCode ?? selectedCompany?.code;
    if (!code) {
      toast({ variant: "destructive", description: "Please select a company" });
      return;
    }
    setLoading(true);
    getCompanyItemPlantStock(code, searchTerm ?? search)
      .then((res: any) => setRows(res?.data?.data || []))
      .catch((error: any) => {
        console.error(error);
        toast({ variant: "destructive", description: error?.response?.data?.message || "Failed to load stock position" });
      })
      .finally(() => setLoading(false));
  };

  const onCompanyChange = (company: any) => {
    setSelectedCompany(company);
    setSearch("");
    if (company?.code) fetchStock(company.code, "");
    else setRows([]);
  };

  // Debounced server-side search once a company is selected.
  useEffect(() => {
    if (!selectedCompany?.code) return;
    const timer = setTimeout(() => fetchStock(selectedCompany.code, search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const handleUpload = async () => {
    if (!selectedCompany?.code) {
      toast({ variant: "destructive", description: "Please select a company" });
      return;
    }
    if (!file) {
      toast({ variant: "destructive", description: "Please select a file to upload" });
      return;
    }
    try {
      setIsUploading(true);
      const res = await uploadStockPosition(file, selectedCompany.code);
      const data = res?.data;
      const unmatched: string[] = data?.warnings?.unmatched_item_codes ?? [];
      // Refresh the table regardless — the records were inserted.
      fetchStock(selectedCompany.code, search);

      if (unmatched.length > 0) {
        // Keep the modal open so the user can read the warning list in place.
        setWarnings(unmatched);
        setFile(null);
        toast({
          description: `Upload successful — ${data?.inserted_count ?? 0} inserted, ${data?.deleted_count ?? 0} removed. ${unmatched.length} item code(s) need attention.`,
        });
      } else {
        toast({ description: `Upload successful — ${data?.inserted_count ?? 0} inserted, ${data?.deleted_count ?? 0} removed` });
        closeUploadModal();
      }
    } catch (error: any) {
      console.error(error);
      toast({ variant: "destructive", description: error?.response?.data?.message || error?.message || "Upload failed" });
    } finally {
      setIsUploading(false);
    }
  };

  const columns = [
    columnHelper.accessor("item_code", { header: () => "Item Code", cell: (info: any) => info.getValue() || "-" }),
    columnHelper.accessor("plant_code", { header: () => "Plant", cell: (info: any) => info.getValue() || "-" }),
    columnHelper.accessor("division_shortcode", { header: () => "Division", cell: (info: any) => info.getValue() || "-" }),
    columnHelper.accessor("stock_available", {
      header: () => "Stock",
      cell: (info: any) => <Badge variant={Number(info.getValue()) > 0 ? "success" : "error"}>{info.getValue()}</Badge>,
    }),
  ];

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="w-[20rem]">
          <Autocomplete
            onSelectionChange={onCompanyChange}
            inputClassName="w-full"
            placeholder="Select company..."
            suggestionList={companies}
            displayKey="name"
            uniqueKey="code"
            defaultValue={selectedCompany?.name || ""}
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="w-[20rem] rounded-md overflow-hidden border border-gray-300 bg-white">
            <Input
              placeholder="Search by item, plant or division"
              debounceTime={500}
              value={search}
              onChange={(event: any) => setSearch(event.target.value.trim())}
              containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
            />
          </div>

          <Button onClick={() => fetchStock()} loaderSize="small" className="h-auto py-2 border">
            Retrieve
          </Button>

          {canEdit && (
            <Button
              onClick={() => {
                setFile(null);
                setWarnings([]);
                setIsUploadOpen(true);
              }}
              disabled={!selectedCompany?.code}
              className="gap-2 border h-auto py-2 transition-all duration-300 ease-in-out hover:bg-gray-100 disabled:opacity-50"
            >
              <img src={UploadIcon} alt="Upload" width={12} height={12} /> Upload
            </Button>
          )}
        </div>
      </div>

      <Table
        title="Stock Position"
        subtitle="View the stock position uploaded for the selected company."
        columns={columns}
        data={rows ?? []}
        isLoading={loading}
        showSearch={false}
      />

      <Modal
        isOpen={isUploadOpen}
        isHeader={true}
        title="Upload Stock Position"
        onCloseModal={closeUploadModal}
        onBack={closeUploadModal}
      >
        <div className="w-[34rem] p-6">
          {warnings.length > 0 ? (
            // Post-upload warning view — the records were inserted, but some item codes
            // aren't configured as alternate item codes for any of the company's products.
            <>
              <header className="bg-[#F8F8F8] p-5 rounded-xl">
                <h3 className="text-xl font-semibold text-[#49484C]">Upload complete — please review</h3>
                <p className="text-base font-medium text-[#8f8e91]">
                  The stock position was uploaded for {selectedCompany?.name}.
                </p>
              </header>

              <main className="p-5">
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
                  <h4 className="text-sm font-semibold text-amber-800">
                    {warnings.length} item code{warnings.length === 1 ? "" : "s"} need attention
                  </h4>
                  <p className="mt-1 text-sm text-amber-700">
                    These item codes were uploaded but are not configured as alternate item codes for any
                    product in {selectedCompany?.name}. They won't be matched to a product until they're added.
                  </p>
                  <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-amber-200 bg-white">
                    <ul className="divide-y divide-amber-100">
                      {warnings.map((code) => (
                        <li key={code} className="px-3 py-2 text-sm font-mono text-[#344054]">
                          {code}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </main>

              <footer className="flex justify-end p-5">
                <Button onClick={closeUploadModal} className="border bg-primary text-white py-[0.62rem] px-8">
                  Done
                </Button>
              </footer>
            </>
          ) : (
            <>
              <header className="bg-[#F8F8F8] p-5 rounded-xl">
                <h3 className="text-xl font-semibold text-[#49484C]">Upload stock position file</h3>
                <p className="text-base font-medium text-[#8f8e91]">
                  Uploading for {selectedCompany?.name}. Choose the file they provided.
                </p>
              </header>

              <main className="p-5">
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-[#344054]">File</p>
                  {/* Hidden native input driven by a styled Button so the control gets the same
                      border + focus-ring feedback as every other button in the app. */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,.ndjson,.txt"
                    className="hidden"
                    onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                  />
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="border h-auto py-2 hover:bg-gray-100"
                    >
                      Choose file
                    </Button>
                    <span className="text-sm text-[#667085] truncate max-w-[20rem]">
                      {file?.name ?? "No file chosen"}
                    </span>
                  </div>
                </div>
              </main>

              <footer className="flex gap-3 justify-between items-center p-5">
                <Button onClick={closeUploadModal} className="border bg-primary text-white flex-1 py-[0.62rem]">
                  Cancel
                </Button>
                <Button
                  isLoading={isUploading}
                  loaderSize="small"
                  disabled={!file}
                  onClick={() => setIsConfirmOpen(true)}
                  className="border flex-1 py-[0.62rem] disabled:bg-gray-400"
                >
                  Upload
                </Button>
              </footer>
            </>
          )}
        </div>
      </Modal>

      {/* Confirmation prompt — the upload deletes all existing rows and re-inserts. */}
      <Modal
        isOpen={isConfirmOpen}
        isHeader={false}
        zIndex={20}
        onCloseModal={() => setIsConfirmOpen(false)}
      >
        <div className="w-[26rem] p-6">
          <h3 className="text-lg font-semibold text-[#101828]">Upload stock position?</h3>
          <p className="mt-2 text-sm text-[#667085]">
            This will permanently delete all existing stock position records for{" "}
            <span className="font-semibold text-[#344054]">{selectedCompany?.name}</span> and replace
            them with the records in this file. This can't be undone.
          </p>
          <footer className="mt-6 flex items-center justify-between gap-3">
            <Button
              onClick={() => setIsConfirmOpen(false)}
              className="border flex-1 py-[0.62rem]"
            >
              Cancel
            </Button>
            <Button
              isLoading={isUploading}
              loaderSize="small"
              onClick={async () => {
                setIsConfirmOpen(false);
                await handleUpload();
              }}
              className="border flex-1 bg-primary text-white py-[0.62rem]"
            >
              Yes, upload
            </Button>
          </footer>
        </div>
      </Modal>
    </>
  );
};

export default StockPosition;
